import { http } from "msw";
import type { Creator, Page, Post, ProductSummary } from "../../api/types";
import { getCollection } from "../db";
import { paginateCursor, search } from "../engine";
import * as viewerState from "../viewer-state";
import { VIEWER_USERNAME } from "../viewer-state";
import {
  allPosts,
  decorateCreator,
  decorateCreatorSummary,
  decorateProductSummary,
  decoratePost,
  mockJson,
  runMutation,
  notFound,
  parsePositiveInt,
  toCreatorSummary,
} from "./shared";

export const creatorsHandlers = [
  http.get("/api/creators", async ({ request }) => {
    const url = new URL(request.url);
    const q = url.searchParams.get("q") ?? "";
    const limit = parsePositiveInt(url.searchParams.get("limit"), 20);
    const suggested = url.searchParams.get("suggested") === "true";

    let creators = (await getCollection<Creator>("creators")).filter((c) => c.username !== VIEWER_USERNAME);
    if (suggested) {
      creators = creators.filter((c) => !viewerState.isFollowingCreator(c.username));
    }
    creators = search(creators, q, [(c) => c.username, (c) => c.displayName]);
    const summaries = creators.slice(0, limit).map((c) => decorateCreatorSummary(toCreatorSummary(c)));
    return mockJson(summaries);
  }),

  http.get("/api/creators/:username", async ({ params }) => {
    const creators = await getCollection<Creator>("creators");
    const creator = creators.find((c) => c.username === params.username);
    if (!creator) return notFound("Creator not found");
    return mockJson(decorateCreator(creator));
  }),

  http.get("/api/creators/:username/posts", async ({ request, params }) => {
    const url = new URL(request.url);
    const cursor = url.searchParams.get("cursor");
    const limit = parsePositiveInt(url.searchParams.get("limit"), 20);
    const posts = (await allPosts()).filter((p) => p.author.username === params.username);
    const page = paginateCursor(posts, cursor, limit);
    const body: Page<Post> = { items: page.items.map(decoratePost), nextCursor: page.nextCursor };
    return mockJson(body);
  }),

  http.get("/api/creators/:username/products", async ({ request, params }) => {
    const url = new URL(request.url);
    const cursor = url.searchParams.get("cursor");
    const limit = parsePositiveInt(url.searchParams.get("limit"), 20);
    const products = await getCollection<ProductSummary>("products");
    const bySeller = products.filter((p) => p.seller.username === params.username);
    const page = paginateCursor(bySeller, cursor, limit);
    const body: Page<ProductSummary> = { items: page.items.map(decorateProductSummary), nextCursor: page.nextCursor };
    return mockJson(body);
  }),

  http.post("/api/creators/:username/follow", async ({ params }) => {
    const username = String(params.username);
    const creators = await getCollection<Creator>("creators");
    const creator = creators.find((c) => c.username === username);
    if (!creator) return notFound("Creator not found");
    return runMutation(() => {
      viewerState.setFollowingCreator(username, true);
      const decorated = decorateCreator(creator);
      return { isFollowing: decorated.isFollowing, followers: decorated.followers };
    });
  }),
  http.delete("/api/creators/:username/follow", async ({ params }) => {
    const username = String(params.username);
    const creators = await getCollection<Creator>("creators");
    const creator = creators.find((c) => c.username === username);
    if (!creator) return notFound("Creator not found");
    return runMutation(() => {
      viewerState.setFollowingCreator(username, false);
      const decorated = decorateCreator(creator);
      return { isFollowing: decorated.isFollowing, followers: decorated.followers };
    });
  }),
];
